/**
 * Oxbo-P client — the domain operational record (events, daily reports, cost,
 * NPT, plan-vs-actual, inbox, saved queries). Read-through cache; Oxbo-Z owns
 * none of it and never recomputes a KPI — every computed number (days,
 * pct_complete, rop_avg, variance, npt roll-ups) is served by P.
 *
 * Base URL is VITE_OXBO_P_URL (p.dev.oxbo.energy by default). MSW serves mocks
 * in standalone mode. Endpoints mirror the live contract exactly.
 */
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import type {
  EventOut,
  DailyReportOut,
  DailyReportCreate,
  DailyReportUpdate,
  ReportCloseResponse,
  CostPivotResponse,
  CostPivotDimension,
  NptRollupResponse,
  PlanVsActualResponse,
  InboxResponse,
  SavedQueryOut,
  AfeLineOut,
  AfeLineCreate,
  AfeLineUpdate,
  SavedQueryCreate,
  SurveyStationOut,
  TubularsResponse,
  BhaRunsResponse,
  ReportMudOut,
  MudCheckOut,
  SafetyDayOut,
  EventSafetyRollupOut,
  PadRollupOut,
  PadSharedCostsResponse,
  PadSharedCostCreate,
  PadSharedCostUpdate,
  PlanTimelineResponse,
} from "./types";

const P_BASE_URL = import.meta.env.VITE_OXBO_P_URL ?? "https://p.dev.oxbo.energy";

/**
 * Query params the live `/v1/inbox` accepts (§10.0, merged on main). `from`/`to`
 * are inclusive date bounds (YYYY-MM-DD); `mine` scopes to the caller's own
 * reports (server reads the JWT subject); `cursor` drives forward pagination.
 */
export interface InboxParams {
  state?: string;
  event_type?: string;
  well_uwi?: string;
  from?: string;
  to?: string;
  mine?: boolean;
  cursor?: string;
  limit?: number;
}

export const pApi = createApi({
  reducerPath: "pApi",
  baseQuery: fetchBaseQuery({ baseUrl: `${P_BASE_URL}/v1` }),
  // Operational data is more volatile than master data — shorter cache window.
  keepUnusedDataFor: 60,
  tagTypes: [
    "Event",
    "Report",
    "Inbox",
    "SavedQuery",
    "Afe",
    "Survey",
    "Tubular",
    "Bha",
    "Mud",
    "Hse",
    "PadRollup",
    "PadSharedCost",
    "PlanTimeline",
  ],
  endpoints: (builder) => ({
    // ── Events (jobs) ─────────────────────────────────────────────────────
    getWellEvents: builder.query<EventOut[], string>({
      query: (wellUwi) => `/wells/${encodeURIComponent(wellUwi)}/events`,
      providesTags: (result, _e, wellUwi) =>
        result
          ? [
              ...result.map((ev) => ({ type: "Event" as const, id: ev.id })),
              { type: "Event" as const, id: `WELL-${wellUwi}` },
            ]
          : [{ type: "Event" as const, id: `WELL-${wellUwi}` }],
    }),

    // ── Daily reports ─────────────────────────────────────────────────────
    getEventReports: builder.query<DailyReportOut[], string>({
      query: (eventId) => `/events/${eventId}/reports`,
      providesTags: (result, _e, eventId) =>
        result
          ? [
              ...result.map((r) => ({ type: "Report" as const, id: r.id })),
              { type: "Report" as const, id: `EVENT-${eventId}` },
            ]
          : [{ type: "Report" as const, id: `EVENT-${eventId}` }],
    }),
    getReport: builder.query<DailyReportOut, string>({
      query: (reportId) => `/reports/${reportId}`,
      providesTags: (_r, _e, id) => [{ type: "Report", id }],
    }),
    createReport: builder.mutation<DailyReportOut, DailyReportCreate>({
      query: (body) => ({ url: "/reports", method: "POST", body }),
      invalidatesTags: (result) =>
        result
          ? [
              { type: "Report", id: `EVENT-${result.event_id}` },
              { type: "Inbox", id: "LIST" },
            ]
          : [{ type: "Inbox", id: "LIST" }],
    }),
    updateReport: builder.mutation<DailyReportOut, { id: string; body: DailyReportUpdate }>({
      query: ({ id, body }) => ({ url: `/reports/${id}`, method: "PATCH", body }),
      invalidatesTags: (result, _e, { id }) =>
        result
          ? [
              { type: "Report", id },
              { type: "Report", id: `EVENT-${result.event_id}` },
            ]
          : [{ type: "Report", id }],
    }),
    closeReport: builder.mutation<ReportCloseResponse, { id: string; isLastOfEvent?: boolean }>({
      query: ({ id, isLastOfEvent }) => ({
        url: `/reports/${id}/close`,
        method: "POST",
        body: { is_last_of_event: isLastOfEvent ?? false },
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "Report", id },
        { type: "Inbox", id: "LIST" },
      ],
    }),

    // ── Server-computed roll-ups (display-only; never recomputed on the FE) ─
    getCostPivot: builder.query<
      CostPivotResponse,
      { eventId: string; dimension?: CostPivotDimension }
    >({
      query: ({ eventId, dimension }) => ({
        url: `/events/${eventId}/cost-pivot`,
        params: dimension ? { dimension } : {},
      }),
    }),
    getPlanVsActual: builder.query<PlanVsActualResponse, string>({
      query: (eventId) => `/events/${eventId}/plan-vs-actual`,
    }),
    getNptRollup: builder.query<NptRollupResponse, string>({
      query: (eventId) => `/events/${eventId}/npt-rollup`,
    }),

    // ── AFE lines (§11) — the line grid + accrual method ──────────────────
    // The grid reads these; create/edit go straight to P, which recomputes the
    // roll-ups (committed / accrued / actual / variance) and returns the line —
    // the FE never recomputes accrual (§21f rule lives server-side).
    getAfeLines: builder.query<AfeLineOut[], string>({
      query: (eventId) => `/events/${eventId}/afe-lines`,
      providesTags: (result, _e, eventId) =>
        result
          ? [
              ...result.map((l) => ({ type: "Afe" as const, id: l.id })),
              { type: "Afe" as const, id: `EVENT-${eventId}` },
            ]
          : [{ type: "Afe" as const, id: `EVENT-${eventId}` }],
    }),
    createAfeLine: builder.mutation<AfeLineOut, { eventId: string; body: AfeLineCreate }>({
      query: ({ eventId, body }) => ({
        url: `/events/${eventId}/afe-lines`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_r, _e, { eventId }) => [{ type: "Afe", id: `EVENT-${eventId}` }],
    }),
    updateAfeLine: builder.mutation<
      AfeLineOut,
      { eventId: string; entityId: string; body: AfeLineUpdate }
    >({
      query: ({ eventId, entityId, body }) => ({
        url: `/events/${eventId}/afe-lines/${entityId}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_r, _e, { eventId, entityId }) => [
        { type: "Afe", id: entityId },
        { type: "Afe", id: `EVENT-${eventId}` },
      ],
    }),
    // ── Surveys (§5) — directional stations, keyed by well UWI ────────────
    // Trajectory columns (tvd/ns/ew/dls) are server-computed (min-curvature on
    // the OXBO-x compute-server); the FE reads them read-only and never
    // recomputes them. Returns the well's station list in MD order.
    getWellSurveys: builder.query<SurveyStationOut[], string>({
      query: (wellUwi) => `/wells/${encodeURIComponent(wellUwi)}/surveys`,
      providesTags: (result, _e, wellUwi) =>
        result
          ? [
              ...result.map((s) => ({ type: "Survey" as const, id: s.id })),
              { type: "Survey" as const, id: `WELL-${wellUwi}` },
            ]
          : [{ type: "Survey" as const, id: `WELL-${wellUwi}` }],
    }),
    // ── Tubulars (§6) — casing/assembly strings + pipe tally, per event ───
    getEventTubulars: builder.query<TubularsResponse, string>({
      query: (eventId) => `/events/${eventId}/tubulars`,
      providesTags: (_r, _e, eventId) => [{ type: "Tubular", id: eventId }],
    }),
    // ── BHA / Bit runs (§7) — runs + nested assembly + bit-ops grid ───────
    getEventBhaRuns: builder.query<BhaRunsResponse, string>({
      query: (eventId) => `/events/${eventId}/bha-runs`,
      providesTags: (_r, _e, eventId) => [{ type: "Bha", id: `EVENT-${eventId}` }],
    }),
    // ── Mud / fluids (§8 / §10.4-b) ───────────────────────────────────────
    // A report's day (checks + products + transactions + volumes) and an
    // event's property-check trend. The FE displays; product line totals are
    // qty × unit_cost (formatting only, no server KPI recomputed).
    getReportMud: builder.query<ReportMudOut, string>({
      query: (reportId) => `/reports/${reportId}/mud`,
      providesTags: (_r, _e, reportId) => [{ type: "Mud", id: `REPORT-${reportId}` }],
    }),
    getEventMudTrend: builder.query<MudCheckOut[], string>({
      query: (eventId) => `/events/${eventId}/mud-checks`,
      providesTags: (_r, _e, eventId) => [{ type: "Mud", id: `EVENT-${eventId}` }],
    }),
    // ── HSE / safety (§9) ─────────────────────────────────────────────────
    // Safety is a DAILY HEADER (LTI counters + day checks), not per-observation
    // action rows. Report endpoint = one day; event endpoint = LTI rollup + the
    // daily HSE trail. All counters are server-computed; the FE only renders.
    getReportSafety: builder.query<SafetyDayOut, string>({
      query: (reportId) => `/reports/${reportId}/safety`,
      providesTags: (_r, _e, reportId) => [{ type: "Hse", id: `REPORT-${reportId}` }],
    }),
    getEventSafety: builder.query<EventSafetyRollupOut, string>({
      query: (eventId) => `/events/${eventId}/safety`,
      providesTags: (_r, _e, eventId) => [{ type: "Hse", id: `EVENT-${eventId}` }],
    }),
    // ── Plan timeline (§13 / §13a / §19) — multi-track plan+actual per level ─
    // Composed (Z) from Oxbo-P events/activities (actual) + program/plan table
    // (planned) + Oxbo-M program-step/phase + Site/Pad grouping. Each level has
    // its own scope key: Fleet = all CPE units × pads over a horizon; Pad =
    // site_id; Well = uwi; Event = event_id (section/stage/interval ops).
    getFleetPlanTimeline: builder.query<PlanTimelineResponse, { division?: string } | void>({
      query: (params) => ({ url: "/fleet/plan-timeline", params: params ?? {} }),
      providesTags: [{ type: "PlanTimeline", id: "FLEET" }],
    }),
    getPadPlanTimeline: builder.query<PlanTimelineResponse, string>({
      query: (siteId) => `/pads/${encodeURIComponent(siteId)}/plan-timeline`,
      providesTags: (_r, _e, siteId) => [{ type: "PlanTimeline", id: `PAD-${siteId}` }],
    }),
    getWellPlanTimeline: builder.query<PlanTimelineResponse, string>({
      query: (uwi) => `/wells/${encodeURIComponent(uwi)}/plan-timeline`,
      providesTags: (_r, _e, uwi) => [{ type: "PlanTimeline", id: `WELL-${uwi}` }],
    }),
    getEventPlanTimeline: builder.query<PlanTimelineResponse, string>({
      query: (eventId) => `/events/${encodeURIComponent(eventId)}/plan-timeline`,
      providesTags: (_r, _e, eventId) => [{ type: "PlanTimeline", id: `EVENT-${eventId}` }],
    }),

    // ── Inbox / saved queries (cross-well) ────────────────────────────────
    // Cursor-paginated cross-well feed. A change to any FILTER starts a fresh
    // list; a change to `cursor` alone appends the next page onto the same cache
    // entry (serializeQueryArgs drops `cursor`, merge concatenates), so the
    // screen renders one growing list without re-fetching earlier pages.
    getInbox: builder.query<InboxResponse, InboxParams | void>({
      query: (params) => ({ url: "/inbox", params: params ?? {} }),
      serializeQueryArgs: ({ queryArgs, endpointName }) => {
        const { cursor: _cursor, ...filters } = queryArgs ?? {};
        return `${endpointName}(${JSON.stringify(filters)})`;
      },
      merge: (current, incoming, { arg }) => {
        // A fresh (no-cursor) load replaces; a paged load appends.
        if (!arg || !arg.cursor) {
          return incoming;
        }
        current.items.push(...incoming.items);
        current.next_cursor = incoming.next_cursor;
      },
      forceRefetch: ({ currentArg, previousArg }) => currentArg?.cursor !== previousArg?.cursor,
      providesTags: [{ type: "Inbox", id: "LIST" }],
    }),
    getSavedQueries: builder.query<SavedQueryOut[], void>({
      query: () => "/saved-queries",
      providesTags: [{ type: "SavedQuery", id: "LIST" }],
    }),
    createSavedQuery: builder.mutation<SavedQueryOut, SavedQueryCreate>({
      query: (body) => ({ url: "/saved-queries", method: "POST", body }),
      invalidatesTags: [{ type: "SavedQuery", id: "LIST" }],
    }),
    deleteSavedQuery: builder.mutation<void, string>({
      query: (id) => ({ url: `/saved-queries/${id}`, method: "DELETE" }),
      invalidatesTags: [{ type: "SavedQuery", id: "LIST" }],
    }),

    // ── Pad view (§12) — per-well time+cost rollup, shared/pad cost lines
    //    (with server-computed allocation splits), and the pad Gantt timeline.
    //    Every number (allocated_shared, per-well split, utilization) is
    //    computed by Oxbo-P; the FE only lays it out.
    getPadRollup: builder.query<PadRollupOut, string>({
      query: (siteId) => `/pads/${encodeURIComponent(siteId)}/rollup`,
      providesTags: (_r, _e, siteId) => [{ type: "PadRollup", id: siteId }],
    }),
    getPadSharedCosts: builder.query<PadSharedCostsResponse, string>({
      query: (siteId) => `/pads/${encodeURIComponent(siteId)}/shared-costs`,
      providesTags: (_r, _e, siteId) => [{ type: "PadSharedCost", id: siteId }],
    }),
    createPadSharedCost: builder.mutation<
      PadSharedCostsResponse,
      { siteId: string; body: PadSharedCostCreate }
    >({
      query: ({ siteId, body }) => ({
        url: `/pads/${encodeURIComponent(siteId)}/shared-costs`,
        method: "POST",
        body,
      }),
      // A shared line changes the pad's per-well allocation, so both roll-ups refetch.
      invalidatesTags: (_r, _e, { siteId }) => [
        { type: "PadSharedCost", id: siteId },
        { type: "PadRollup", id: siteId },
      ],
    }),
    updatePadSharedCost: builder.mutation<
      PadSharedCostsResponse,
      { siteId: string; lineId: string; body: PadSharedCostUpdate }
    >({
      query: ({ siteId, lineId, body }) => ({
        url: `/pads/${encodeURIComponent(siteId)}/shared-costs/${lineId}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_r, _e, { siteId }) => [
        { type: "PadSharedCost", id: siteId },
        { type: "PadRollup", id: siteId },
      ],
    }),
    // The pad Gantt (§12/§13) — multi-track plan+actual for the pad's CPE + wells.
    getPadTimeline: builder.query<PlanTimelineResponse, string>({
      query: (siteId) => `/pads/${encodeURIComponent(siteId)}/timeline`,
    }),
  }),
});

export const {
  useGetWellEventsQuery,
  useGetEventReportsQuery,
  useGetReportQuery,
  useCreateReportMutation,
  useUpdateReportMutation,
  useCloseReportMutation,
  useGetCostPivotQuery,
  useGetPlanVsActualQuery,
  useGetNptRollupQuery,
  useGetAfeLinesQuery,
  useCreateAfeLineMutation,
  useUpdateAfeLineMutation,
  useGetWellSurveysQuery,
  useGetEventTubularsQuery,
  useGetEventBhaRunsQuery,
  useGetReportMudQuery,
  useGetEventMudTrendQuery,
  useGetReportSafetyQuery,
  useGetEventSafetyQuery,
  useGetInboxQuery,
  useGetSavedQueriesQuery,
  useCreateSavedQueryMutation,
  useDeleteSavedQueryMutation,
  useGetPadRollupQuery,
  useGetPadSharedCostsQuery,
  useCreatePadSharedCostMutation,
  useUpdatePadSharedCostMutation,
  useGetPadTimelineQuery,
  useGetFleetPlanTimelineQuery,
  useGetPadPlanTimelineQuery,
  useGetWellPlanTimelineQuery,
  useGetEventPlanTimelineQuery,
} = pApi;
